/**
 * User Utility Functions
 * Helper functions for user profile management and validation
 */

/**
 * Check if user has a valid delivery address
 * Supports multiple data structures for backward compatibility
 * @param {Object} userProfile - User profile from Firestore
 * @returns {boolean} - Whether user has valid address coordinates
 */
export const hasValidDeliveryAddress = (userProfile) => {
  if (!userProfile) {
    console.log('❌ hasValidDeliveryAddress: No user profile provided');
    return false;
  }
  
  // First check direct lat/lng on user profile (most legacy format)
  if (userProfile.latitude && userProfile.longitude) {
    console.log('✅ hasValidDeliveryAddress: Found direct lat/lng on profile');
    return true;
  }
  
  // Check if deliveryAddress exists
  if (!userProfile.deliveryAddress) {
    console.log('❌ hasValidDeliveryAddress: No deliveryAddress property found');
    return false;
  }

  const { deliveryAddress } = userProfile;
  console.log('🔍 Checking deliveryAddress:', deliveryAddress);

  try {
    // Extra safety check: first verify deliveryAddress exists and is an object
    if (!deliveryAddress || typeof deliveryAddress !== 'object') {
      console.log('❌ hasValidDeliveryAddress: deliveryAddress is missing or not an object');
      return false;
    }

    // Debug the structure of deliveryAddress
    console.log('🔍 deliveryAddress structure:', {
      hasCoordinatesProperty: 'coordinates' in deliveryAddress,
      coordinatesType: deliveryAddress.coordinates ? typeof deliveryAddress.coordinates : 'undefined',
      hasLatLng: deliveryAddress.lat && deliveryAddress.lng,
      hasLatitudeLongitude: deliveryAddress.latitude && deliveryAddress.longitude,
    });

    // Check for multiple possible address data structures with safe access
    const hasCoordinates = (
      // New structure: deliveryAddress.coordinates?.latitude/longitude
      (deliveryAddress.coordinates && 
       typeof deliveryAddress.coordinates === 'object' && 
       deliveryAddress.coordinates.latitude && 
       deliveryAddress.coordinates.longitude) ||
      // Legacy structure: deliveryAddress.lat/lng
      (deliveryAddress.lat && deliveryAddress.lng) ||
      // Alternative structure: deliveryAddress.latitude/longitude
      (deliveryAddress.latitude && deliveryAddress.longitude)
    );
    
    console.log('🔍 hasValidDeliveryAddress result:', hasCoordinates);
    return Boolean(hasCoordinates);
  } catch (error) {
    console.error('❌ Error checking address validity:', error);
    return false;
  }
};

/**
 * Get normalized coordinates from user's delivery address
 * @param {Object} userProfile - User profile from Firestore
 * @returns {Object|null} - Normalized coordinates {lat, lng} or null if not found
 */
export const getNormalizedCoordinates = (userProfile) => {
  if (!hasValidDeliveryAddress(userProfile)) {
    return null;
  }

  const { deliveryAddress } = userProfile;

  // Check if deliveryAddress exists
  if (!deliveryAddress) {
    console.log('❌ getNormalizedCoordinates: No deliveryAddress property found');
    return null;
  }

  // Check new structure first with safe access
  if (deliveryAddress?.coordinates?.latitude && deliveryAddress?.coordinates?.longitude) {
    return {
      lat: deliveryAddress.coordinates.latitude,
      lng: deliveryAddress.coordinates.longitude
    };
  }

  // Check legacy structure
  if (deliveryAddress.lat && deliveryAddress.lng) {
    return {
      lat: deliveryAddress.lat,
      lng: deliveryAddress.lng
    };
  }

  // Check alternative structure
  if (deliveryAddress.latitude && deliveryAddress.longitude) {
    return {
      lat: deliveryAddress.latitude,
      lng: deliveryAddress.longitude
    };
  }

  return null;
};

/**
 * Get user's home route based on user type
 * @param {string} userType - User type (admin, delivery, customer)
 * @returns {string} - Route path for user's home page
 */
export const getUserHomeRoute = (userType) => {
  switch (userType) {
    case 'admin':
      return '/admin';
    case 'delivery':
      return '/delivery';
    case 'customer':
    default:
      return '/';
  }
};

/**
 * Check if user needs profile completion
 * @param {Object} userProfile - User profile from Firestore
 * @returns {boolean} - Whether user needs to complete profile
 * 
 * NOTE: Profile completion is disabled. Users will provide name and address during checkout.
 */
export const needsProfileCompletion = (userProfile) => {
  try {
    // Profile completion is no longer required
    // Users will provide their details during checkout when adding delivery address
    console.log('✅ needsProfileCompletion: Profile completion disabled - will collect info at checkout');
    return false;
  } catch (error) {
    console.error('❌ Error in needsProfileCompletion:', error);
    // Even on error, don't force profile completion
    return false;
  }
};

/**
 * Debug log user profile information
 * @param {Object} userProfile - User profile from Firestore
 * @param {string} context - Context for the debug log
 */
export const debugUserProfile = (userProfile, context = 'User Profile') => {
  if (process.env.REACT_APP_DEBUG_MODE === 'true') {
    console.log(`🔍 ${context}:`);
    console.log('📱 User Type:', userProfile?.userType);
    console.log('📍 Has Address:', hasValidDeliveryAddress(userProfile));
    console.log('📍 Coordinates:', getNormalizedCoordinates(userProfile));
    console.log('📄 Full Profile:', userProfile);
  }
};

export default {
  hasValidDeliveryAddress,
  getNormalizedCoordinates,
  getUserHomeRoute,
  needsProfileCompletion,
  debugUserProfile
};
